import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase, getSessionToken, storeSessionToken, clearSessionToken } from './supabaseClient';
import { logAction } from './utils/systemLog';
import { setPushUser } from './utils/pushNotifications';

const STORAGE_KEY = 'crm_current_user';

// Phase 2: log in through the session-login Edge Function so the database gets
// a signed token identifying the employee. Off until the rollout turns it on
// (REACT_APP_USE_SESSION_TOKEN=true at build time); with it off the login is
// exactly the old login_secure call.
const USE_SESSION_TOKEN = process.env.REACT_APP_USE_SESSION_TOKEN === 'true';

const DISABLED_MESSAGE = 'الحساب ده اتوقف. كلّم الإدارة لو محتاج تدخل.';

const userFromRow = (row) => ({
  id: row.id,
  name: row.name,
  phone: row.phone,
  roleId: row.role_id,
  roleSlug: row.role_slug || null,
  roleName: row.role_name || null,
  teamId: row.team_id || null,
  avatarUrl: row.avatar_url || null,
});

export const ROLE_LABELS = {
  admin: 'أدمن',
  super_admin: 'سوبر أدمن',
  accounts_manager: 'مدير حسابات',
  sales_manager: 'مدير مبيعات',
  sales_rep: 'بائع',
  customer_service: 'خدمة عملاء',
  hr: 'HR',
  developer: 'مبرمج',
  marketing: 'تسويق',
  warehouse: 'المخزن',
  delivery_rep: 'مندوب توصيل',
  outside_sales: 'مبيعات خارجية',
  team_leader: 'تيم ليدر',
  administrative: 'ادارى',
};

export const ROLE_DESCRIPTIONS = {
  admin: 'يشوف كل حاجة، وله كل الصلاحيات (عرض، تعديل، حذف، نقل ملكية العملاء).',
  super_admin: 'نفس صلاحيات الأدمن بالظبط في كل حاجة من غير أي استثناء.',
  accounts_manager:
    'زي الأدمن بالظبط في كل حاجة (يشوف ويعدّل ويحذف كل حاجة في النظام)، ما عدا صفحة صلاحيات المستخدمين مش شايفها.',
  sales_manager:
    'يشوف بيانات البائعين وخدمة العملاء، يقدر ينقل عملاء بين المستخدمين، ويعدّل ويحذف بيانات العملاء. يشوف لوحة تاسكات المبيعات بس.',
  sales_rep:
    'يشوف عملاءه هو بس، يسجل أوردرات وملاحظات ويعدّل بيانات عملائه، من غير حذف. يشوف لوحة تاسكات المبيعات بس.',
  customer_service:
    'يشوف كل العملاء، يسجل أوردر أو شكوى ويعدّل بيانات العملاء، من غير حذف. يشوف لوحة تاسكات المبيعات بس.',
  hr: 'ميشوفش أي حاجة في النظام غير صفحة المتقدمين للوظايف بس.',
  developer: 'ميشوفش أي حاجة في النظام غير لوحة تاسكات قسم البرمجة بس.',
  marketing: 'ميشوفش أي حاجة في النظام غير لوحة تاسكات قسم التسويق بس.',
  warehouse: 'ميشوفش أي حاجة في النظام غير صفحة تجهيز الأوردرات بس.',
  delivery_rep: 'ميشوفش أي حاجة في النظام غير الأوردرات اللي معاه هو بس، وبيسجل عليها تم التسليم أو مؤجل والتحصيل.',
  outside_sales:
    'نفس صلاحيات وشاشات البائع (عملاءه هو، أوردرات، ملاحظات)، وكمان نفس صلاحيات وشاشات مندوب التوصيل (الأوردرات اللي معاه للتوصيل، تسليم أو تأجيل والتحصيل).',
  team_leader:
    'يشوف بياناته هو وبيانات فريقه بس في صفحة العملاء والمقدمين للتوظيف (لازم يتحدد له فريق من صفحة الصلاحيات). يشوف كمان لوحة تاسكات المبيعات، استفسارات العملاء، والأوردرات بشكل كامل.',
  administrative:
    'يشوف ويتعامل مع لوحة التاسكات بكل الأقسام، وصفحة سجل الحضور، وصفحة الدبلكيت. في المتقدمين للوظايف: يضيف ويرفع ويعدّل وينقل الليدز للمسؤولين، من غير حذف. في العملاء والاستفسارات والأوردرات وتجهيز الأوردرات والمنتجات وزيارات اليوم ومتابعات اليوم وأوردراتي: عرض بس، من غير تعديل أو أي إجراء.',
};

// Roles with admin-level data permissions (view/edit/delete everywhere),
// excluding access to user/role management itself.
// super_admin is deliberately included here too - it's meant to behave
// exactly like 'admin' everywhere. Any admin-only check that does NOT go
// through this helper (grep the codebase for standalone 'admin' checks)
// needs 'super_admin' added there too, kept as a separate literal so a
// future change scoped to just 'admin' doesn't silently also affect it.
export const ADMIN_LEVEL_ROLES = ['admin', 'accounts_manager', 'super_admin'];
export const isAdminLevel = (roleSlug) => ADMIN_LEVEL_ROLES.includes(roleSlug);

// outside_sales combines a sales rep's permissions/screens with a delivery
// rep's permissions/screens, so anywhere either role is checked, it should
// count as that role too.
export const SALES_REP_LIKE_ROLES = ['sales_rep', 'outside_sales'];
export const DELIVERY_REP_LIKE_ROLES = ['delivery_rep', 'outside_sales'];
export const isSalesRepLike = (roleSlug) => SALES_REP_LIKE_ROLES.includes(roleSlug);
export const isDeliveryRepLike = (roleSlug) => DELIVERY_REP_LIKE_ROLES.includes(roleSlug);

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    try {
      return JSON.parse(stored);
    } catch (err) {
      console.error('Error parsing stored session:', err);
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
  });

  // A restored session sends its stored token over Realtime too (HTTP requests
  // pick it up automatically in supabaseClient.js). With the token flag on, an
  // expired/missing token means the session is over - sign in again.
  useEffect(() => {
    if (!currentUser) return;
    const token = getSessionToken();
    if (token) {
      supabase.realtime.setAuth(token);
    } else if (USE_SESSION_TOKEN) {
      localStorage.removeItem(STORAGE_KEY);
      setCurrentUser(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the device's push subscription tied to the logged-in user, both on
  // explicit login/logout and when a session is restored from localStorage.
  useEffect(() => {
    setPushUser(currentUser?.id || null);
  }, [currentUser?.id]);

  // Dynamic per-page permissions pilot (page_permissions / page_permission_extras).
  // super_admin always bypasses this entirely - see hasPageAccess/hasPageExtra
  // below - so it can never be locked out by a misconfigured row here.
  const [pagePermissions, setPagePermissions] = useState({});
  const [pageExtras, setPageExtras] = useState({});
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  useEffect(() => {
    if (!currentUser?.roleId || currentUser.roleSlug === 'super_admin') {
      setPagePermissions({});
      setPageExtras({});
      setPermissionsLoaded(true);
      return;
    }
    setPermissionsLoaded(false);
    (async () => {
      const [permsRes, extrasRes] = await Promise.all([
        supabase.from('page_permissions').select('page_key, can_view, can_edit, can_delete').eq('role_id', currentUser.roleId),
        supabase.from('page_permission_extras').select('page_key, action_key, allowed').eq('role_id', currentUser.roleId),
      ]);
      if (permsRes.error) console.error('Error fetching page permissions:', permsRes.error);
      if (extrasRes.error) console.error('Error fetching page permission extras:', extrasRes.error);
      const permsMap = {};
      (permsRes.data || []).forEach((row) => {
        permsMap[row.page_key] = row;
      });
      const extrasMap = {};
      (extrasRes.data || []).forEach((row) => {
        if (!extrasMap[row.page_key]) extrasMap[row.page_key] = {};
        extrasMap[row.page_key][row.action_key] = row.allowed;
      });
      setPagePermissions(permsMap);
      setPageExtras(extrasMap);
      setPermissionsLoaded(true);
    })();
  }, [currentUser?.roleId, currentUser?.roleSlug]);

  // action is 'view' | 'edit' | 'delete'.
  const hasPageAccess = (pageKey, action) => {
    if (currentUser?.roleSlug === 'super_admin') return true;
    const key = action === 'view' ? 'can_view' : action === 'edit' ? 'can_edit' : 'can_delete';
    return !!pagePermissions[pageKey]?.[key];
  };

  const hasPageExtra = (pageKey, actionKey) => {
    if (currentUser?.roleSlug === 'super_admin') return true;
    return !!pageExtras[pageKey]?.[actionKey];
  };

  // Returns { row } on success, { error } with a user-facing message on a real
  // login failure, or null when the token service isn't available (not set up
  // yet / network) so the caller can fall back to the plain login.
  const loginWithSessionToken = async (phone, pin) => {
    let res;
    try {
      res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/session-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ phone, pin }),
      });
    } catch (err) {
      return null;
    }
    if (res.status === 429) {
      return { error: 'محاولات دخول غلط كتير. الرقم اتقفل مؤقت، حاول تاني بعد شوية.' };
    }
    if (res.status === 401) return { error: 'رقم التليفون أو الرقم السري غلط' };
    if (res.status === 403) return { error: DISABLED_MESSAGE };
    if (!res.ok) return null;
    const body = await res.json().catch(() => null);
    if (!body?.token || !body?.user) return null;
    storeSessionToken(body.token, body.expires_at);
    return { row: body.user };
  };

  const login = async (phone, pin) => {
    if (USE_SESSION_TOKEN) {
      const result = await loginWithSessionToken(phone, pin);
      if (result?.error) return { error: result.error };
      if (result?.row) {
        const user = userFromRow(result.row);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
        setCurrentUser(user);
        logAction(user, { action: 'تسجيل دخول', entityType: 'user', entityId: user.id, description: `${user.name} سجل دخول` });
        return { user };
      }
      // Token service unavailable: fall through to the plain login below.
    }
    // Verified server-side by login_secure (a SECURITY DEFINER function) -
    // the pin column itself is no longer readable by the anon key, so the
    // match can't happen in a plain client-side select anymore.
    const { data, error } = await supabase.rpc('login_secure', { p_phone: phone, p_pin: pin });
    const row = data?.[0];

    if (error?.message?.includes('account locked')) {
      return { error: 'محاولات دخول غلط كتير. الرقم اتقفل مؤقت، حاول تاني بعد شوية.' };
    }
    if (error?.message?.includes('account disabled')) {
      return { error: DISABLED_MESSAGE };
    }
    if (error || !row) {
      return { error: 'رقم التليفون أو الرقم السري غلط' };
    }

    const user = userFromRow(row);

    localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    setCurrentUser(user);
    logAction(user, { action: 'تسجيل دخول', entityType: 'user', entityId: user.id, description: `${user.name} سجل دخول` });
    return { user };
  };

  const logout = () => {
    clearSessionToken();
    localStorage.removeItem(STORAGE_KEY);
    setCurrentUser(null);
  };

  // A user who was disabled while already signed in on a phone gets signed out
  // the next time this check runs (on open, and every few minutes).
  useEffect(() => {
    if (!currentUser?.id) return undefined;
    let cancelled = false;
    const check = async () => {
      const { data, error } = await supabase.from('users').select('is_active').eq('id', currentUser.id).maybeSingle();
      if (cancelled || error || !data || data.is_active !== false) return;
      logout();
      window.alert(DISABLED_MESSAGE);
    };
    check();
    const interval = setInterval(check, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  const updateCurrentUser = (patch) => {
    setCurrentUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...patch };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  return (
    <AuthContext.Provider
      value={{ currentUser, login, logout, updateCurrentUser, hasPageAccess, hasPageExtra, permissionsLoaded }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
